import { useState, useEffect, useCallback, useRef } from "react";
import moment from "moment";
import { pdfjs } from "react-pdf";
import CaseService from "../services/CaseService";

if (!pdfjs.GlobalWorkerOptions.workerSrc) {
  pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;
}

const IMAGE_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "bmp",
  "webp",
  "tiff",
  "svg",
]);

const CONVERTIBLE_EXTENSIONS = new Set([
  "docx",
  "doc",
  "txt",
  "xlsx",
  "xls",
  "csv",
]);

const getExtension = (url) => {
  if (!url) return "pdf";
  const clean = url.split("?")[0].split("#")[0];
  const ext = clean.split(".").pop();
  return ext ? ext.toLowerCase() : "pdf";
};

const determineSource = (doc) => {
  const claimant =
    doc.ownerRole === "claimant" ||
    doc.claimantView === true ||
    doc.claimantView === "true";

  const respondent =
    doc.ownerRole === "respondent" ||
    doc.respondentView === true ||
    doc.respondentView === "true";

  if (claimant) return "Claimant";
  if (respondent) return "Respondent";

  return "Neutral";
};

const transform = (doc) => {
  const ext = getExtension(doc.url?.url);
  const isImage = IMAGE_EXTENSIONS.has(ext);

  return {
    id: doc.id,
    name: doc.name || "Untitled Document",
    pages: isImage
      ? 1
      : typeof doc.totalPages === "number" && doc.totalPages > 0
        ? doc.totalPages
        : null,
    category: doc.url?.uploadType || "Document",
    source: determineSource(doc),
    date: doc.created_at ? moment(doc.created_at).format("DD MMM YYYY") : "—",
    ext,
    fileUrl: doc.url?.url || null,
    visibilityType: doc.visibilityType,
    isImage,
    originalDoc: doc,
  };
};

const buildQuery = (sourceFilter, page = 1, perPage = 100) => {
  let q = `?page=${page}&perPage=${perPage}`;
  if (sourceFilter === "Claimant") q += "&claimantView=true";
  if (sourceFilter === "Respondent") q += "&respondentView=true";
  if (sourceFilter === "Neutral") q += "&commonView=true";
  return q;
};

const fetchPageCount = async (url) => {
  if (!url) return null;
  try {
    const pdf = await pdfjs.getDocument({ url }).promise;
    return pdf.numPages;
  } catch (err) {
    console.warn("[useDocuments] Could not count pages for", url, err);
    return null;
  }
};

const PDF_CONVERT_API = "https://staging-api.justact.co.in/pdf_convert";

const convertDocuments = async (docs) => {
  if (!docs.length) return [];

  try {
    const payload = {
      documents: docs.map((doc) => ({ url: doc.fileUrl })),
    };

    const response = await fetch(PDF_CONVERT_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!response.ok) throw new Error(`Conversion failed: ${response.status}`);

    const result = await response.json();

    if (result.url) {
      return [{ id: docs[0].id, convertedUrl: result.url }];
    }

    return (
      result.documents?.map((conv, index) => ({
        id: docs[index].id,
        convertedUrl: conv.url || conv.convertedUrl || null,
      })) || []
    );
  } catch (err) {
    console.error("[useDocuments] Document conversion failed:", err);
    return [];
  }
};

const useDocuments = (caseId, sourceFilter = "All") => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const abortRef = useRef(false);

  const fetchDocuments = useCallback(async () => {
    if (!caseId) return;

    abortRef.current = true;
    abortRef.current = false;

    setLoading(true);
    setError(null);

    try {
      const query = buildQuery(sourceFilter);
      const res = await CaseService.getcaseDocuments(caseId, query);

      if (!res?.data || !Array.isArray(res.data)) {
        setDocuments([]);
        return;
      }

      const visibleDocs = res.data.filter(
        (doc) => doc.visibilityType === "all",
      );

      let transformed = visibleDocs.map(transform);

      const needsConversion = transformed.filter(
        (doc) =>
          !doc.isImage && CONVERTIBLE_EXTENSIONS.has(doc.ext) && doc.fileUrl,
      );

      setDocuments(transformed);

      if (needsConversion.length > 0) {
        const conversions = await convertDocuments(needsConversion);

        setDocuments((prev) =>
          prev.map((doc) => {
            const conv = conversions.find((c) => c.id === doc.id);
            if (conv?.convertedUrl) {
              return { ...doc, fileUrl: conv.convertedUrl, ext: "pdf" };
            }
            return doc;
          }),
        );

        const nowNeedsProbe = transformed
          .filter((doc) => !doc.isImage && doc.pages === null && doc.fileUrl)
          .map((doc) => {
            const conv = conversions.find((c) => c.id === doc.id);
            return {
              ...doc,
              fileUrl: conv?.convertedUrl || doc.fileUrl,
            };
          });

        await probePageCounts(nowNeedsProbe);
      } else {
        const needsProbe = transformed.filter(
          (doc) =>
            !doc.isImage &&
            doc.pages === null &&
            doc.ext === "pdf" &&
            doc.fileUrl,
        );
        await probePageCounts(needsProbe);
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load documents. Please try again.");
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [caseId, sourceFilter]);

  const probePageCounts = async (needsProbe) => {
    if (needsProbe.length === 0) return;

    const CONCURRENCY = 5;
    const queue = [...needsProbe];

    const runBatch = async () => {
      while (queue.length > 0) {
        if (abortRef.current) return;

        const batch = queue.splice(0, CONCURRENCY);
        const results = await Promise.all(
          batch.map(async (doc) => ({
            id: doc.id,
            pages: await fetchPageCount(doc.fileUrl),
          })),
        );

        if (abortRef.current) return;

        setDocuments((prev) =>
          prev.map((doc) => {
            const resolved = results.find((r) => r.id === doc.id);
            if (!resolved || resolved.pages === null) return doc;
            return { ...doc, pages: resolved.pages };
          }),
        );
      }
    };

    await runBatch();
  };

  useEffect(() => {
    fetchDocuments();
    return () => {
      abortRef.current = true;
    };
  }, [fetchDocuments]);

  const docsWithPages = documents
    .map((d) =>
      !d.isImage && d.pages === null && d.fileUrl ? { ...d, pages: 1 } : d,
    )
    .filter((d) => d.isImage || d.pages !== null);

  return { documents, docsWithPages, loading, error, refetch: fetchDocuments };
};

export default useDocuments;
